import { getFindingsView } from 'Apis/library';
import { Category, FindingStatus, Page, FindingsInsightsDto } from '@ampsec/platform-client';
import { useCallback, useMemo } from 'react';
import { resolvePaginationTotalRows } from '../FindingsGrid/findingsGridPerfPagination';
import { normalizeFindingsFilterCriteria } from 'Core-utils/helpers/findingsUserAssociationFilter';
import { readUserNameColumnHasUserAssociation, readUserNameColumnSearchTerms } from './userNameColumnFilter';
import {
    type ServerDatasourceBuildContext,
    buildOffsetLimit,
    buildSingleSort,
    pickUrlSetFilters,
    readTrimmedTextFilter,
    readTrimmedUrlParam,
    useServerSideDatasource,
} from '../AgGridBase';
import { readLastUpdatedAtHint } from 'Core-utils/helpers/pageHints';

/** API expects filter keys with { $in: string[] }. provider_name may use displayValue. */
/* eslint-disable camelcase */
const COLUMN_TO_API_FIELD: Record<string, string> = {
    severity: 'severity',
    finding_status: 'finding_status',
    finding_category: 'finding_category',
    finding_kind: 'finding_kind',
    finding_title: 'finding_title',
    provider_name: 'provider_name',
    user_name: 'user_name',
    user_department: 'user_department',
    finding_tags: 'finding_tags',
};
/* eslint-enable camelcase */

export interface FindingsServerSideDataSourceProps {
    /** Base filter criteria passed to getFindingsView (e.g. from page/context). */
    filterCriteria: Record<string, any>;
    /** Additional filter from URL/AG-Grid state (merged with filterCriteria for the request body). */
    urlFilter?: Record<string, any>;
    /** Optional override: filter by finding category (e.g. from parent page). */
    findingCategory?: string | string[];
    /** Optional override: filter by finding kind (e.g. from parent page). */
    findingKind?: string | string[];
    /** Map provider display names to API displayValue when needed (provider_name column). */
    providerDisplayValueMap?: (selectedValues: string[]) => string[];
    onRowCountChange?: (filtered: number, total: number) => void;
    onLastUpdatedAtChange?: (lastUpdatedAt?: string) => void;
}

/**
 * Builds filters for getFindingsView from AG-Grid filter model.
 * Converts set filter values and text filter to { field: { $in: string[] } } or search param.
 */
function buildFiltersFromModel(
    filterModel: Record<string, any>,
    providerDisplayValueMap?: (values: string[]) => string[],
): Record<string, { $in: string[] }> {
    const filters: Record<string, { $in: string[] }> = {};
    if (!filterModel) {
        return filters;
    }

    Object.entries(filterModel).forEach(([colId, model]) => {
        const apiField = COLUMN_TO_API_FIELD[colId] ?? colId;
        if (colId === 'q') {
            return;
        }
        if (model?.filterType === 'set' && Array.isArray(model.values) && model.values.length > 0) {
            let values = model.values as string[];
            if (colId === 'provider_name' && providerDisplayValueMap) {
                values = providerDisplayValueMap(values);
            }
            filters[apiField] = { $in: values };
        }
        if (
            (model?.filterType === 'departmentMultiSelect' || model?.filterType === 'tagMultiSelect') &&
            Array.isArray(model.values) &&
            model.values.length > 0
        ) {
            filters[apiField] = { $in: model.values as string[] };
        }
    });

    return filters;
}

function filterIncludesValue(filterValue: unknown, expectedValue: string): boolean {
    if (Array.isArray(filterValue)) {
        return filterValue.includes(expectedValue);
    }
    if (typeof filterValue === 'string') {
        return filterValue === expectedValue;
    }
    if (filterValue && typeof filterValue === 'object' && '$in' in filterValue) {
        const inValues = (filterValue as { $in?: unknown }).$in;
        return Array.isArray(inValues) && inValues.includes(expectedValue);
    }
    return false;
}

export function useFindingsServerSideDataSource({
    filterCriteria,
    urlFilter = {},
    findingCategory,
    findingKind,
    providerDisplayValueMap,
    onRowCountChange,
    onLastUpdatedAtChange,
}: FindingsServerSideDataSourceProps) {
    const normalizedFilterCriteria = useMemo(() => normalizeFindingsFilterCriteria(filterCriteria), [filterCriteria]);

    const buildQuery = useCallback(
        ({
            startRow,
            endRow,
            sortModel,
            filterModel,
            urlFilter: incomingUrlFilter,
        }: ServerDatasourceBuildContext<Record<string, any>, Record<string, any>>) => {
            const { offset, limit } = buildOffsetLimit(startRow, endRow);
            const sort = buildSingleSort(sortModel) ?? { severity: 'ASC' as const };
            const filtersFromModel = buildFiltersFromModel(filterModel || {}, providerDisplayValueMap);
            const filtersFromUrl = pickUrlSetFilters(incomingUrlFilter, [
                'severity',
                'finding_status',
                'finding_category',
                'finding_kind',
                'finding_title',
                'provider_name',
                'finding_tags',
            ]);
            const rawFindingTags = incomingUrlFilter?.finding_tags;
            if (typeof rawFindingTags === 'string' && rawFindingTags.includes(',')) {
                const tagValues = rawFindingTags
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
                if (tagValues.length > 0) {
                    filtersFromUrl['finding_tags'] = { $in: tagValues };
                }
            }

            const mergedFilters: Record<string, unknown> = { ...filtersFromUrl, ...filtersFromModel };
            if (readUserNameColumnHasUserAssociation(filterModel || {}) && mergedFilters['user_id'] === undefined) {
                mergedFilters['user_id'] = { $neq: null };
            }
            if (findingCategory !== undefined) {
                const arr = Array.isArray(findingCategory) ? findingCategory : [findingCategory];
                mergedFilters['finding_category'] = { $in: arr };
            }
            if (findingKind !== undefined) {
                const arr = Array.isArray(findingKind) ? findingKind : [findingKind];
                mergedFilters['finding_kind'] = { $in: arr };
            }

            const qTerms = [
                readTrimmedTextFilter(filterModel || {}, 'q'),
                ...readUserNameColumnSearchTerms(filterModel || {}),
                readTrimmedTextFilter(filterModel || {}, 'device_display_value'),
                readTrimmedUrlParam(incomingUrlFilter, 'q'),
                readTrimmedUrlParam(incomingUrlFilter, 'device_display_value'),
            ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
            const q = qTerms.length > 0 ? Array.from(new Set(qTerms)).join(' ') : undefined;
            const findingTagsIn =
                mergedFilters.finding_tags &&
                typeof mergedFilters.finding_tags === 'object' &&
                '$in' in mergedFilters.finding_tags
                    ? (mergedFilters.finding_tags as { $in?: unknown }).$in
                    : undefined;
            const findingTagValues = Array.isArray(findingTagsIn)
                ? findingTagsIn.filter((tag): tag is string => typeof tag === 'string')
                : [];
            const hasMicrotrainingTagFilter = findingTagValues.some((tag) => tag.startsWith('Microtraining_'));
            const shouldIncludeOpenMicrotraining =
                hasMicrotrainingTagFilter ||
                (filterIncludesValue(mergedFilters.finding_category, Category.MICROTRAINING) &&
                    filterIncludesValue(mergedFilters.finding_status, FindingStatus.OPEN));

            return {
                offset,
                limit,
                ...mergedFilters,
                ...(q ? { q } : {}),
                ...(!shouldIncludeOpenMicrotraining ? { includeOpenMicrotraining: false } : {}),
                sort,
            };
        },
        [findingCategory, findingKind, providerDisplayValueMap],
    );

    const fetchRows = useCallback(
        async (queryParams: Record<string, any>) => {
            try {
                const response = (await getFindingsView(
                    normalizedFilterCriteria,
                    queryParams,
                )) as Page<FindingsInsightsDto>;
                const rows = response?.data ?? [];
                const offset =
                    typeof response?.hints?.offset === 'number' ? response.hints.offset : (queryParams.offset ?? 0);
                const limit =
                    typeof response?.hints?.limit === 'number' && response.hints.limit > 0
                        ? response.hints.limit
                        : (queryParams.limit ?? 50);
                const page = Math.floor(offset / limit) + 1;
                let total = resolvePaginationTotalRows(response, page, limit);
                // Defensive: never tell SSRM rowCount=0 when this page has rows (e.g. stale count: 0).
                if (rows.length > 0 && total === 0) {
                    total = offset + rows.length + (rows.length >= limit ? limit : 0);
                }
                onLastUpdatedAtChange?.(readLastUpdatedAtHint(response?.hints));
                return { rows, total };
            } catch (error) {
                onLastUpdatedAtChange?.(undefined);
                throw error;
            }
        },
        [normalizedFilterCriteria, onLastUpdatedAtChange],
    );

    const datasource = useServerSideDatasource<Record<string, any>, any, Record<string, any>>({
        urlFilter,
        buildQuery,
        fetchRows,
        onRowCountChange,
    });

    return { datasource };
}
